import { i18n } from "../../../../lib/globalization/i18n.js";
import { Component } from "../../../../lib/ui/Component.js";
import { ComboBox } from "../../../../lib/ui/controls/ComboBox.js";
import { Button } from "../../../../lib/ui/controls/Button.js";
import { ModalDialog } from "../../../../lib/ui/controls/dialogs/ModalDialog.js";
import { Text } from "../../../../lib/ui/controls/Text.js";
import { Grid } from "../../../../lib/ui/controls/Grid.js";
import { sortedCollectionBy } from "../../../../lib/data/data-utils.js";
import {
    FILE_REPOSITORY_PERMISSION_FIELDS,
    FILE_REPOSITORY_PERMISSIONS,
    hasFileRepositoryPermissionValue,
} from "../utils/file-repository-permissions.js";

const PERMISSION_LABELS = Object.freeze({
    open: 'Open',
    modify: 'Meta',
    writeItems: 'Soubory',
    writeFolders: 'Složky',
    alterPermissions: 'Alter',
    alterOwnership: 'Owner',
    grantPermissions: 'Grant',
});

const PERMISSION_ICONS = Object.freeze({
    open: 'folder-open',
    modify: 'pencil',
    writeItems: 'file-plus',
    writeFolders: 'folder-plus',
    alterPermissions: 'circle-user-circle-check',
    alterOwnership: 'user-gear',
    grantPermissions: 'shield-user',
});

export class FolderPermissionPanel extends Component {

    static PERMISSIONS = FILE_REPOSITORY_PERMISSIONS;
    static PERMISSION_FIELDS = FILE_REPOSITORY_PERMISSION_FIELDS;

    constructor(parent, options = {}) {

        super(parent, options);

        const {
            getFolderData = () => null,
            getFileRepositoryManager = () => null,
            onOpenFolder = () => null,
            onReloadRequested = async () => null,
        } = options || {};

        this.getFolderDataValue = getFolderData;
        this.getFileRepositoryManagerValue = getFileRepositoryManager;
        this.onOpenFolderValue = onOpenFolder;
        this.onReloadRequestedValue = onReloadRequested;

        this.permissionContext = null;
        this.permissionRows = [];
        this.permissionLoadError = null;
        this.permissionDrafts = {};
        this.permissionUsers = [];
        this.permissionRoles = [];

    };

    getFolderData() {
        return typeof this.getFolderDataValue === 'function' ? this.getFolderDataValue.call(this) : null;
    };

    getFileRepositoryManager() {
        return typeof this.getFileRepositoryManagerValue === 'function' ? this.getFileRepositoryManagerValue.call(this) : null;
    };

    async reloadOwnerModule() {
        if (typeof this.onReloadRequestedValue === 'function')
            return this.onReloadRequestedValue.call(this);
        return null;
    };

    openFolder(folderUid) {
        if (typeof this.onOpenFolderValue === 'function')
            this.onOpenFolderValue.call(this, folderUid);
    };

    handleError(error) {
        this.getApp().handleError(error);
    };

    getPermissionContext() {
        return this.permissionContext || null;
    };

    getPermissionRows() {
        return Array.isArray(this.permissionRows) ? this.permissionRows : [];
    };

    canShowPermissionEditor() {
        return !!this.getPermissionContext()?.ownsPermissionContext;
    };

    canDetachPermissions() {
        return !!this.getPermissionContext()?.canDetachPermissions;
    };

    canInheritPermissions() {
        return !!this.getPermissionContext()?.canInheritPermissions;
    };

    canNavigateToPermissionOwner() {
        const context = this.getPermissionContext();
        return !!context?.permissionFolderUid && context.permissionFolderUid !== this.getFolderData()?.uid;
    };

    canEditPermissionRows() {
        const context = this.getPermissionContext();
        return !!context?.ownsPermissionContext && (!!context?.canAlterPermissions || !!context?.canGrantPermissions);
    };

    getPermissionEditableMask() {
        const context = this.getPermissionContext();
        let mask = 0;

        if (context?.canAlterPermissions) {
            mask |= FolderPermissionPanel.PERMISSIONS.open
                | FolderPermissionPanel.PERMISSIONS.modify
                | FolderPermissionPanel.PERMISSIONS.writeItems
                | FolderPermissionPanel.PERMISSIONS.writeFolders;
        }

        if (context?.canGrantPermissions) {
            mask |= FolderPermissionPanel.PERMISSIONS.alterPermissions
                | FolderPermissionPanel.PERMISSIONS.grantPermissions
                | FolderPermissionPanel.PERMISSIONS.alterOwnership;
        }

        return mask;
    };

    canEditPermissionBit(permissionBit) {
        return hasFileRepositoryPermissionValue(this.getPermissionEditableMask(), permissionBit);
    };

    getPermissionDraft(rowUid) {
        return this.permissionDrafts?.[rowUid] || null;
    };

    isPermissionRowDirty(rowUid) {
        return !!this.getPermissionDraft(rowUid);
    };

    getPermissionRowUid(row) {
        return row?.uid || null;
    };

    getPermissionRowPermissions(row) {
        return this.getPermissionDraft(this.getPermissionRowUid(row))?.permissions ?? row?.permissions ?? 0;
    };

    updatePermissionDraft(row, nextPermissions) {
        const rowUid = this.getPermissionRowUid(row);
        if (!rowUid)
            return;

        const normalizedPermissions = Number(nextPermissions) || 0;
        const currentPermissions = Number(row.permissions) || 0;

        if (normalizedPermissions === currentPermissions) {
            const nextDrafts = { ...this.permissionDrafts };
            delete nextDrafts[rowUid];
            this.permissionDrafts = nextDrafts;
        } else {
            this.permissionDrafts = {
                ...this.permissionDrafts,
                [rowUid]: {
                    uid: rowUid,
                    permissions: normalizedPermissions,
                },
            };
        }

        this.invalidate();
    };

    togglePermissionRowBit(row, permissionBit) {
        if (!this.canEditPermissionBit(permissionBit))
            return;

        const currentPermissions = this.getPermissionRowPermissions(row);
        const nextPermissions = hasFileRepositoryPermissionValue(currentPermissions, permissionBit)
            ? (currentPermissions & ~permissionBit)
            : (currentPermissions | permissionBit);

        this.updatePermissionDraft(row, nextPermissions);
    };

    cancelPermissionRowChanges(rowId) {
        if (!this.permissionDrafts[rowId])
            return;

        const nextDrafts = { ...this.permissionDrafts };
        delete nextDrafts[rowId];
        this.permissionDrafts = nextDrafts;
        this.invalidate();
    };

    resetPermissionEditorState() {
        this.permissionContext = null;
        this.permissionRows = [];
        this.permissionLoadError = null;
        this.permissionDrafts = {};
    };

    async ensurePermissionSubjectsLoaded() {

        if (!Array.isArray(this.permissionUsers) || this.permissionUsers.length === 0)
            this.permissionUsers = [...(await this.queryPermissionSubjectCodelist('core/users/codelist'))];

        if (!Array.isArray(this.permissionRoles) || this.permissionRoles.length === 0)
            this.permissionRoles = [...(await this.queryPermissionSubjectCodelist('core/roles/codelist'))];

    };

    async queryPermissionSubjectCodelist(path) {

        const service = this.getServices().getServiceFactory().createCodelistService(null, {
            codelistPath: path,
        });
        const data = await service.queryCodelistData();

        if (Array.isArray(data))
            return data;

        return [];

    };

    async savePermissionRow(row) {

        const rowUid = this.getPermissionRowUid(row);
        if (!rowUid || !this.isPermissionRowDirty(rowUid))
            return;

        const folderUid = this.getFolderData()?.uid || null;
        if (!folderUid) {
            this.handleError('Aktuální složka není k dispozici pro uložení oprávnění.');
            return;
        }

        const app = this.getApp();
        app?.setBusyFlag?.();

        try {
            await this.getFileRepositoryManager().updateFolderPermission(folderUid, rowUid, {
                permissions: this.getPermissionDraft(rowUid)?.permissions ?? row.permissions ?? 0,
            });
            await this.reloadOwnerModule();
        } catch (error) {
            this.handleError(error);
        } finally {
            app?.clearBusyFlag?.();
        }

    };

    async deletePermissionRow(row) {

        const rowUid = this.getPermissionRowUid(row);
        if (!rowUid)
            return;

        const folderUid = this.getFolderData()?.uid || null;
        if (!folderUid) {
            this.handleError('Aktuální složka není k dispozici pro smazání oprávnění.');
            return;
        }

        const app = this.getApp();
        const control = this;
        const subjectLabel = row?.roleLabel || row?.userLabel || 'vybraný grant';

        const modal = new ModalDialog(null, {
            title: i18n.t('module.browser.record_delete_title'),
            controls: [
                new Text(null, { text: `Odstranit oprávnění pro "${subjectLabel}"?` }),
            ],
            buttons: [
                new Button(null, {
                    label: i18n.t('button.delete.label'),
                    icon: '#confirm',
                    onClick: deleteConfirmed
                }),
                new Button(null, {
                    label: i18n.t('button.cancel.label'),
                    icon: '#cancel',
                    css: '.outlined',
                    onClick: () => { modal.hide(); }
                }),
            ]
        });

        modal.show(app);

        async function deleteConfirmed() {

            modal.hide();
            app?.setBusyFlag?.();

            try {
                await control.getFileRepositoryManager().deleteFolderPermission(folderUid, rowUid);
                await control.reloadOwnerModule();
            } catch (error) {
                control.handleError(error);
            } finally {
                app?.clearBusyFlag?.();
            }

        }

    };

    async detachPermissions() {
        return this.runPermissionContextAction('detach');
    };

    async inheritPermissions() {
        return this.runPermissionContextAction('inherit');
    };

    async runPermissionContextAction(action) {

        const folderUid = this.getFolderData()?.uid || null;
        if (!folderUid) {
            this.handleError('Aktuální složka není k dispozici pro správu oprávnění.');
            return;
        }

        const app = this.getApp();
        app?.setBusyFlag?.();

        try {
            if (action === 'detach')
                await this.getFileRepositoryManager().detachFolderPermissions(folderUid);
            else if (action === 'inherit')
                await this.getFileRepositoryManager().inheritFolderPermissions(folderUid);
            await this.reloadOwnerModule();
        } catch (error) {
            this.handleError(error);
        } finally {
            app?.clearBusyFlag?.();
        }

    };

    async showCreatePermissionDialog(subjectType = 'user') {

        const folderUid = this.getFolderData()?.uid || null;
        if (!folderUid) {
            this.handleError('Aktuální složka není k dispozici pro přidání oprávnění.');
            return;
        }

        await this.ensurePermissionSubjectsLoaded();

        const app = this.getApp();
        const control = this;
        const assignedSubjectValues = new Set(this.getPermissionRows()
            .map((row) => subjectType === 'role' ? row?.roleUid : row?.userUid)
            .filter((value) => !!value));
        const subjectItems = sortedCollectionBy(subjectType === 'role' ? this.permissionRoles : this.permissionUsers, 'label')
            .map((item) => {
                const assigned = assignedSubjectValues.has(item?.value);
                const label = item?.label ?? item?.value ?? '?';
                return {
                    ...item,
                    label: assigned ? `${label} — již přiřazeno` : label,
                    disabled: !!item?.disabled || assigned,
                };
            });
        const subjectLabel = subjectType === 'role' ? 'Role' : 'Uživatel';
        const subjectProp = subjectType === 'role' ? 'roleUid' : 'userUid';

        if (subjectItems.length === 0) {
            this.handleError(`Nejsou k dispozici žádné ${subjectType === 'role' ? 'role' : 'uživatelé'} pro přidání oprávnění.`);
            return;
        }

        const subjectCombo = new ComboBox(null, {
            value: null,
            items: () => subjectItems,
            itemValueProp: 'value',
            itemLabelProp: 'label',
            emptyOptionLabel: `Vyber ${subjectType === 'role' ? 'roli' : 'uživatele'}`,
        });

        const modal = new ModalDialog(null, {
            title: subjectType === 'role' ? 'Přidat roli' : 'Přidat uživatele',
            controls: [
                (
                    new Grid(null, {})
                        .cell({ controls: [new Text(null, { text: `Vyber ${subjectType === 'role' ? 'roli' : 'uživatele'}, kterému chceš založit grant. Již přiřazené položky jsou v seznamu zobrazené jako neaktivní.` })] })
                        .cell({ controls: [new Text(null, { text: subjectLabel })] })
                        .cell({ controls: [subjectCombo] })
                )
            ],
            buttons: [
                new Button(null, {
                    label: 'Přidat',
                    icon: '#confirm',
                    onClick: createConfirmed
                }),
                new Button(null, {
                    label: i18n.t('button.cancel.label'),
                    icon: '#cancel',
                    css: '.outlined',
                    onClick: () => { modal.hide(); }
                }),
            ]
        });

        modal.show(app);

        async function createConfirmed() {

            const subjectValue = subjectCombo.getValue();
            if (!subjectValue) {
                control.handleError(`Vyber ${subjectType === 'role' ? 'roli' : 'uživatele'}.`);
                return;
            }

            const selectedSubject = subjectItems.find((item) => String(item?.value ?? '') === String(subjectValue ?? ''));
            if (selectedSubject?.disabled) {
                control.handleError(`${subjectType === 'role' ? 'Role' : 'Uživatel'} už má oprávnění přidělena.`);
                return;
            }

            modal.hide();
            app?.setBusyFlag?.();

            try {
                await control.getFileRepositoryManager().createFolderPermission(folderUid, {
                    [subjectProp]: subjectValue,
                    permissions: 0,
                });
                await control.reloadOwnerModule();
            } catch (error) {
                control.handleError(error);
            } finally {
                app?.clearBusyFlag?.();
            }

        }

    };

    async _refresh(options, activeRoute) {

        await super._refresh(options, activeRoute);

        const folderUid = this.getFolderData()?.uid || null;
        if (!folderUid) {
            this.resetPermissionEditorState();
            return true;
        }

        this.permissionLoadError = null;

        try {
            const [context, dataset] = await Promise.all([
                this.getFileRepositoryManager().getFolderPermissionContext(folderUid),
                this.getFileRepositoryManager().getFolderPermissions(folderUid),
            ]);

            this.permissionContext = context || null;
            this.permissionRows = Array.isArray(dataset?.items) ? dataset.items : [];
            this.permissionDrafts = {};

            return true;
        } catch (error) {
            this.permissionContext = null;
            this.permissionRows = [];
            this.permissionDrafts = {};
            this.permissionLoadError = true;
            this.handleError(error);
            return false;
        }

    };

    _render(r, vm) {

        const control = this;

        if (!this.getFolderData())
            return r.div();

        return r.section(null, [
            renderPermissionHeader(),
            ...renderPermissionBody(),
        ]);

        function renderPermissionHeader() {
            return r.row({ css: 'border medium-round medium-padding wrap' }, [
                r.span('bold', ['Oprávnění složky']),
                ' ',
                r.span('small', [
                    controlStatusLabel()
                ]),
            ]);
        }

        function controlStatusLabel() {
            const context = control.getPermissionContext();
            if (!context)
                return 'nejsou dostupná';
            if (context.ownsPermissionContext)
                return `spravována zde: ${context.permissionFolderName || context.folderName}`;
            return `jsou převzata ze složky "${context.permissionFolderName || '?'}"`;
        }

        function renderPermissionBody() {
            if (control.permissionLoadError) {
                return r.table({ css: 'lines' }, [
                    r.tbody([
                        r.trow([
                            r.tcell({ props: { colspan: 10 } }, [
                                r.empty({
                                    title: 'Oprávnění se nepodařilo načíst',
                                    subtitle: 'Správu oprávnění se teď nepodařilo načíst.',
                                    icon: r.icon('lock', 'fa-3x'),
                                }),
                            ]),
                        ]),
                    ]),
                ]);
            }

            const blocks = [
                renderPermissionToolbar(),
            ];

            if (control.canShowPermissionEditor()) {
                blocks.push(renderPermissionTable());
            } else {
                blocks.push(renderSharedPermissionInfo());
            }

            return blocks;
        }

        function renderPermissionToolbar() {
            const canEditRows = control.canEditPermissionRows();

            return r.nav(null, [
                control.canDetachPermissions()
                    ? r.button('Odpojit oprávnění', 'link-broken', {
                        css: 'outlined',
                        props: { type: 'button', onclick: () => { control.detachPermissions(); } },
                    })
                    : null,
                control.canInheritPermissions()
                    ? r.button('Zdědit oprávnění', 'link', {
                        css: 'outlined',
                        props: { type: 'button', onclick: () => { control.inheritPermissions(); } },
                    })
                    : null,
                control.canNavigateToPermissionOwner()
                    ? r.button('Přejít na vlastníka oprávnění', 'arrow-up-to-dotted-line', {
                        css: 'outlined',
                        props: { type: 'button', onclick: () => { control.openFolder(control.getPermissionContext()?.permissionFolderUid || null); } },
                    })
                    : null,
                r.spacer(),
                control.canShowPermissionEditor()
                    ? r.button('Přidat uživatele', '#user', {
                        props: {
                            type: 'button',
                            disabled: !canEditRows,
                            title: canEditRows ? null : 'Nemáš oprávnění upravovat granty v této složce.',
                            onclick: () => { control.showCreatePermissionDialog('user'); },
                        },
                    })
                    : null,
                control.canShowPermissionEditor()
                    ? r.button('Přidat roli', '#user-group', {
                        props: {
                            type: 'button',
                            disabled: !canEditRows,
                            title: canEditRows ? null : 'Nemáš oprávnění upravovat granty v této složce.',
                            onclick: () => { control.showCreatePermissionDialog('role'); },
                        },
                    })
                    : null,
            ]);
        }

        function renderSharedPermissionInfo() {
            const context = control.getPermissionContext();
            return r.table({ css: 'lines' }, [
                r.tbody([
                    r.trow([
                        r.tcell({ props: { colspan: 10 } }, [
                            r.empty({
                                title: 'Tato složka sdílí oprávnění',
                                subtitle: context?.permissionFolderName
                                    ? `Oprávnění složky jsou převzata ze složky "${context.permissionFolderName}".`
                                    : 'Oprávnění složky jsou převzata z jiné složky.',
                                icon: r.icon('link', 'fa-3x'),
                            }),
                        ]),
                    ]),
                ]),
            ]);
        }

        function renderPermissionTable() {
            return r.table({ css: 'lines' }, [
                r.thead([
                    r.trow([
                        r.thcell([r.icon('users'), ' Role / ', r.icon('user'), ' Uživatel']),
                        ...FolderPermissionPanel.PERMISSION_FIELDS.map((field) => r.thcell('min center-align', [
                            field.key ? r.icon(PERMISSION_ICONS[field.key], 'fa-2x') : field.label
                        ])),
                        r.thcell('min right-align', ['Akce']),
                    ]),
                ]),
                r.tbody(
                    control.getPermissionRows().map((row) => renderPermissionRow(row))
                ),
            ]);
        }

        function renderPermissionRow(row) {
            const rowUid = control.getPermissionRowUid(row);
            const rowPermissions = control.getPermissionRowPermissions(row);
            const rowDirty = control.isPermissionRowDirty(rowUid);

            return r.trow([
                r.tcell([
                    (row.roleLabel ? r.icon('users') : (row.userLabel ? r.icon('user') : '')),
                    ' ',
                    (row.roleLabel || row.userLabel || '-')

                ]),
                ...FolderPermissionPanel.PERMISSION_FIELDS.map((field) => {
                    const checked = hasFileRepositoryPermissionValue(rowPermissions, field.value);
                    const disabled = !control.canEditPermissionBit(field.value);

                    return r.tcell('min center-align', [
                        r.checkBox(checked, '', {
                            css: 'inline-block',
                            props: {
                                disabled,
                                title: disabled ? 'Tento bit oprávnění teď nemůžeš měnit.' : null,
                                onchange: () => { control.togglePermissionRowBit(row, field.value); },
                            },
                        }),
                    ]);
                }),
                r.tcell('min right-align', [
                    rowDirty
                        ? r.iconButton('#confirm', {
                            css: 'transparent circle',
                            props: { type: 'button', title: 'Uložit změny', onclick: () => { control.savePermissionRow(row); } },
                        })
                        : null,
                    rowDirty
                        ? r.iconButton('#cancel', {
                            css: 'transparent circle',
                            props: { type: 'button', title: 'Zrušit změny', onclick: () => { control.cancelPermissionRowChanges(rowUid); } },
                        })
                        : null,
                    !rowDirty
                        ? r.iconButton('#delete', {
                            css: 'transparent circle',
                            props: {
                                type: 'button',
                                title: 'Odstranit grant',
                                disabled: !control.canEditPermissionRows(),
                                onclick: () => { control.deletePermissionRow(row); },
                            },
                        })
                        : null,
                ]),
            ]);
        }

    }

}
